/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Text,
  VStack,
  Circle,
  useDisclosure,
  Tooltip,
  Box,
} from '@chakra-ui/react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormWatch,
  UseFormSetValue,
  UseFieldArrayUpdate,
} from 'react-hook-form';
import DeleteIcon from '@/assets/svg/delete.svg?react';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import InfoCircleIcon from '@/assets/svg/info-circle.svg?react';
import { useGetColors, useRemoveVariant } from '@/services/addProduct';
import { AxiosError } from 'axios';
import ImageUploadSection from './ImageUploadSection';
import SizeDetailsSection from './SizeDetails';
import Asterisk from '../common/Asterisk';
import { type FormData } from '@/services/addProduct/types';
import CustomSelect from '../common/CustomSelect';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import { useState } from 'react';
import { IProductEditResponse } from '@/services/manageProduct/types';

interface Option {
  id: string;
  label: string | React.ReactNode;
  hex_code?: string;
  searchLabel?: string;
}

interface VariantItemProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  item: FormData['variants'][number];
  index: number;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<FormData>;
  watch: UseFormWatch<FormData>;
  product?: IProductEditResponse;
  setValue: UseFormSetValue<FormData>;
  id?: string;
  showToast: (type: 'success' | 'error', message: string) => void;
  totalVariants: number;
  isPrimaryVariant?: boolean;
  vendorCurrency?: string;
}

const VariantItem = ({
  control,
  errors,
  item,
  index,
  remove,
  update,
  watch,
  product,
  setValue,
  id,
  showToast,
  totalVariants,
  isPrimaryVariant = false,
  vendorCurrency,
}: VariantItemProps) => {
  const { data: colors } = useGetColors();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutateAsync: removeVariant } = useRemoveVariant();
  const [deleteVariantInfo, setDeleteVariantInfo] = useState<{
    variantIndex: number;
    sizeIds: number[];
  } | null>(null);

  const getSelectedColors = (currentVariantIndex: number) => {
    const selectedColors: string[] = [];
    const variants = watch('variants');
    variants.forEach((variant: FormData['variants'][number], idx: number) => {
      if (idx !== currentVariantIndex && variant?.color?.name) {
        selectedColors.push(variant.color.name);
      }
    });
    return selectedColors;
  };

  const handleVariantDelete = (variantIndex: number, sizeIds: number[]) => {
    if (totalVariants === 1) {
      showToast('error', 'At least one variant is required');
      return;
    }
    if (isPrimaryVariant) {
      showToast('error', 'Cannot delete the primary variant');
      return;
    }
    setDeleteVariantInfo({ variantIndex, sizeIds });
    onOpen();
  };

  const handleVariantDeleteConfirm = () => {
    if (!deleteVariantInfo) return;

    const { variantIndex, sizeIds } = deleteVariantInfo;

    if (id) {
      removeVariant(sizeIds)
        .then(() => {
          remove(variantIndex);
          onClose();
          setDeleteVariantInfo(null);
          showToast('success', 'Variant removed successfully');
        })
        .catch((error: unknown) => {
          if (error instanceof AxiosError) {
            showToast('error', error?.response?.data?.[0] || 'Failed to remove variant');
          }
          onClose();
          setDeleteVariantInfo(null);
        });
    } else {
      remove(variantIndex);
      onClose();
      setDeleteVariantInfo(null);
      showToast('success', 'Variant removed successfully');
    }
  };

  const colorOptions: Option[] = (colors || []).map((color: any) => ({
    id: JSON.stringify({ id: color.id, name: color.name, hex_code: color.hex_code }),
    label: (
      <Flex align="center" gap={2}>
        <Circle size="12px" bg={color.hex_code} />
        <Text>{color.name}</Text>
      </Flex>
    ),
    searchLabel: color.name,
    hex_code: color.hex_code,
  }));

  return (
    <Flex
      border="1px solid"
      borderColor="border.default60"
      bg="background.white"
      borderRadius="lg"
      p={4}
      direction="column"
      gap={4}
    >
      <Flex justify={'space-between'}>
        <HStack align="center" gap={1}>
          <Text fontSize="md" fontWeight="semibold">
            Variant {index + 1}
          </Text>
          <Tooltip
            label="This will be shown as the default variant when a user visits the product"
            placement="auto"
            hasArrow
            openDelay={200}
            closeDelay={300}
            bg={'background.white'}
            textColor={'primary'}
            border={'1px'}
            borderColor={'border.default'}
            borderRadius={'md'}
            gutter={8}
          >
            <Box display="inline-block" position="relative">
              {isPrimaryVariant && <Icon as={InfoCircleIcon} boxSize={5} cursor="help" />}
            </Box>
          </Tooltip>
        </HStack>

        <Icon
          cursor={isPrimaryVariant ? 'not-allowed' : 'pointer'}
          onClick={() =>
            handleVariantDelete(
              index,
              item.sizes.map((size) => size.id).filter((id) => id !== 0),
            )
          }
          color={'status.error'}
          boxSize={6}
          opacity={isPrimaryVariant ? 0.4 : 1}
        >
          <DeleteIcon />
        </Icon>
      </Flex>

      <VStack spacing={4} align="stretch">
        <HStack spacing={4} align={'center'} justifyContent={'space-between'}>
          <FormControl isInvalid={!!errors.variants?.[index]?.color}>
            <FormLabel
              fontSize="sm"
              color={errors.variants?.[index]?.color ? 'status.error' : 'text.secondary'}
            >
              Color
              <Asterisk />
            </FormLabel>
            <Controller
              name={`variants.${index}.color`}
              control={control}
              render={({ field }) => {
                const selectedColors = getSelectedColors(index);

                const availableOptions = colorOptions.filter((opt) => {
                  const parsed = JSON.parse(opt.id);
                  return !selectedColors.includes(parsed.name);
                });

                let currentSelectedOption = null;
                if (field.value?.name && field.value?.hex_code) {
                  currentSelectedOption = colorOptions.find((opt) => {
                    const parsed = JSON.parse(opt.id);
                    return (
                      parsed.name === field.value.name && parsed.hex_code === field.value.hex_code
                    );
                  });
                }

                const selectedValue = currentSelectedOption ? currentSelectedOption.id : null;

                return (
                  <CustomSelect
                    options={availableOptions}
                    placeholder="Select Color"
                    selectedValue={selectedValue}
                    onChange={(value: string) => {
                      const selected = JSON.parse(value);
                      field.onChange({
                        id: selected.id,
                        name: selected.name,
                        hex_code: selected.hex_code,
                      });
                    }}
                    size="md"
                    isFullWidth={true}
                    isSearchable={true}
                  />
                );
              }}
            />
            <FormErrorMessage>{errors.variants?.[index]?.color?.name?.message}</FormErrorMessage>
          </FormControl>
        </HStack>

        <ImageUploadSection
          control={control}
          errors={errors}
          index={index}
          product={product}
          setValue={setValue}
          item={item}
        />

        <SizeDetailsSection
          control={control}
          errors={errors}
          index={index}
          item={item}
          update={update}
          id={id}
          showToast={showToast}
          watch={watch}
          setValue={setValue}
          vendorCurrency={vendorCurrency}
        />
      </VStack>

      <CustomConfirmationModal
        isLoading={false}
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setDeleteVariantInfo(null);
        }}
        onConfirm={handleVariantDeleteConfirm}
        icon={ConfirmDeleteIcon}
        title="Delete Variant"
        description="Are you sure you want to delete this variant? This action will also remove all associated sizes."
        buttonText="Confirm"
      />
    </Flex>
  );
};

export default VariantItem;
